import {Trans} from '@lingui/macro';
import React, {FC, ReactNode, useMemo} from 'react';

import {IconName} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {PaletteColor} from '../../../../../../libs/shared/ui/src/styles/colorStyles';
import {UserTheme, useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {isWildcardToken} from '../../../../../../srcCommon/inboxes/search/searchHelpers';
import {TeammateModel} from '../../../../../models/api/teammateModel';
import {
  InboxesSearchSuggestionsBaseProps,
  InboxesSuggestionsDropdownItem,
  InboxesSuggestionsDropdownItemIcon,
  InboxesSuggestionsDropdownItemPrimaryContent,
  InboxesSuggestionsDropdownSection,
  InboxesSuggestionsDropdownSectionTitle,
  InboxesSuggestionsDropdownSectionWrapper,
} from './inboxesSearchSuggestionsDropdownCommons';

/*
 * Props.
 */

interface QuickFilterSuggestion {
  suggestion: string;
  icon: IconName;
  color?: PaletteColor;
  content: ReactNode;
}

interface InboxesSearchSuggestionsDropdownQuickFiltersProps extends InboxesSearchSuggestionsBaseProps {
  user: TeammateModel;
}

/*
 * Component.
 */

export const InboxesSearchSuggestionsDropdownQuickFilters: FC<
  InboxesSearchSuggestionsDropdownQuickFiltersProps
> = ({user, query, activeToken, onSuggestionSelect}) => {
  const theme = useUserTheme();
  const quickFilters = useMemo(() => makeQuickFilters(user, theme), [user, theme]);

  if (!isWildcardToken(activeToken, query)) {
    return null;
  }

  return (
    <InboxesSuggestionsDropdownSectionWrapper>
      <InboxesSuggestionsDropdownSection>
        <InboxesSuggestionsDropdownSectionTitle>
          <Trans id="inboxes-search-quick-filters">Quick filters</Trans>
        </InboxesSuggestionsDropdownSectionTitle>
        {quickFilters.map(({suggestion, icon, color, content}) => (
          <InboxesSuggestionsDropdownItem
            key={suggestion}
            onSelect={(e, key) =>
              onSuggestionSelect(suggestion, key, {insertWhiteSpace: true, isQuickFilter: true})
            }
          >
            <InboxesSuggestionsDropdownItemIcon iconName={icon} color={color && color.shade40} />
            <InboxesSuggestionsDropdownItemPrimaryContent>
              {content}
            </InboxesSuggestionsDropdownItemPrimaryContent>
          </InboxesSuggestionsDropdownItem>
        ))}
      </InboxesSuggestionsDropdownSection>
    </InboxesSuggestionsDropdownSectionWrapper>
  );
};

/*
 * Helpers.
 */

const makeQuickFilters = (user: TeammateModel, theme: UserTheme): ReadonlyArray<QuickFilterSuggestion> => [
  {
    suggestion: 'is:unread',
    icon: 'unreadSmall',
    color: theme.palette.blue,
    content: <Trans id="search-quickfilters-unread">Unread</Trans>,
  },
  {
    suggestion: 'has:reminder',
    icon: 'snoozeFilledSmall',
    color: theme.palette.orange,
    content: <Trans id="search-quickfilters-snoozed">Snoozed</Trans>,
  },
  {
    suggestion: `mention:${user.alias}`,
    icon: 'mentionSmall',
    color: theme.palette.blue,
    content: <Trans id="search-quickfilters-mention">My mentions</Trans>,
  },
  {
    suggestion: 'has:attachment',
    icon: 'attachmentSmall',
    content: <Trans id="search-quickfilters-attachments">Attachment</Trans>,
  },
];
